<script setup lang="ts">
import { useCurrentElement } from '@vueuse/core';
import { useForm } from 'vee-validate';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { TokenizedPaymentMethod } from '@/api/paymentMethod';
import CreditCardForm from '@/components/account/auto-delivery/details/CreditCardForm.vue';
import PaymentMethodOption from '@/components/account/auto-delivery/details/PaymentMethodOption.vue';
import RadioPill from '@/components/base/form/RadioPill.vue';
import Modal from '@/components/base/layout/Modal.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import AddressForm from '@/components/checkout/AddressForm.vue';
import { useAddressFields, useForm as formUtils } from '@/composables/useForm';
import { usePayment } from '@/composables/usePayment';
import { NutsAddress } from '@/utils/address';

interface FailedPaymentMethod {
  description: string;
  type: string;
  expiration: string;
}

const props = withDefaults(
  defineProps<{
    allowSameMethod?: boolean;
    canSetAsDefault?: boolean;
    declined?: boolean;
    failedPaymentMethod?: FailedPaymentMethod;
    hasBillingAddress?: boolean;
    isLoading?: boolean;
    isOpen: boolean;
  }>(),
  {
    hasBillingAddress: true,
  },
);

const emit = defineEmits<{
  'handle-close': [];
  'handle-update': [paymentMethod: TokenizedPaymentMethod, done: () => void];
}>();

const element = useCurrentElement();
const store = useStore();
const { getSavedPaymentMethods } = usePayment(store, ref(false));
const { initializeAddress } = useAddressFields();
const { validatorPassed } = formUtils();

const braintreeToken = ref('');
const defaultPaymentMethod = ref<TokenizedPaymentMethod>();
const newBillingAddress = ref<NutsAddress>(initializeAddress());
const newPaymentMethod = ref<TokenizedPaymentMethod>();
const paymentMethodList = ref<HTMLDivElement>();
const savedPaymentMethods = ref<TokenizedPaymentMethod[]>([]);
const selectedPaymentMethod = ref<TokenizedPaymentMethod>();

const addressFormModel = useForm();
const validBillingAddress = computed(() => validatorPassed(addressFormModel.meta.value));

const disabledConfirmationButton = computed(() => {
  const paymentMethod = selectedPaymentMethod.value ?? newPaymentMethod.value;

  const isPaymentMethodInvalid =
    !paymentMethod ||
    (!props.allowSameMethod &&
      defaultPaymentMethod.value &&
      paymentMethod.nonce === defaultPaymentMethod.value?.nonce);
  if (selectedPaymentMethod.value) {
    return isPaymentMethodInvalid;
  }
  const isNewBillingAddressInvalid = props.hasBillingAddress
    ? false
    : newPaymentMethod.value && (!newBillingAddress.value || !validBillingAddress.value);
  return isPaymentMethodInvalid || isNewBillingAddressInvalid;
});

const failedTokenizedPaymentMethod = computed(() => {
  const filter = savedPaymentMethods.value.filter((paymentMethod) => {
    if (!props.failedPaymentMethod) {
      return false;
    }

    const failedDescription = props.failedPaymentMethod.description;
    const thirdParty = ['Apple Pay', 'PayPal'];
    const currentPaymentIsThirdParty = thirdParty.includes(paymentMethod.type);
    if (paymentMethod.type === props.failedPaymentMethod.type && currentPaymentIsThirdParty) {
      return true;
    }

    if (thirdParty.includes(props.failedPaymentMethod.type)) {
      return false;
    }

    const failedExpiration = props.failedPaymentMethod.expiration;
    const expiration = `${paymentMethod.details.expirationMonth}/${paymentMethod.details.expirationYear}`;
    return (
      failedDescription.indexOf(paymentMethod.details.lastFour) !== -1 &&
      failedExpiration === expiration
    );
  });
  return filter && filter.length > 0 ? filter[0] : undefined;
});

const fetchPaymentMethods = async () => {
  const paymentMethods = (await getSavedPaymentMethods()) as TokenizedPaymentMethod[];

  const filteredPaymentMethods = paymentMethods.filter(
    (m) => m.type === 'CreditCard' || m.type === 'Apple Pay' || m.type === 'PayPal',
  );
  const defaultPayment = filteredPaymentMethods.find((m) => m.default);

  savedPaymentMethods.value = filteredPaymentMethods;
  selectedPaymentMethod.value = defaultPayment ?? filteredPaymentMethods[0];
  defaultPaymentMethod.value = defaultPayment;
};

const defaultOrFirstPaymentMethod = computed<TokenizedPaymentMethod | undefined>(
  () => defaultPaymentMethod.value ?? savedPaymentMethods.value[0],
);

onBeforeMount(async () => {
  const { useBraintree } = await import('@/stores/braintree');
  const braintreeStore = useBraintree();
  braintreeToken.value = (await braintreeStore.loadAuthToken()) ?? '';

  await fetchPaymentMethods();
});

watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      await fetchPaymentMethods();
      newBillingAddress.value = initializeAddress();
    }
  },
);

watch(
  () => props.declined,
  async (declined) => {
    if (declined) {
      element.value.children[0].scroll({
        top: 0,
        behavior: 'smooth',
      });
    }
  },
);

const handleConfirm = () => {
  const paymentMethod = selectedPaymentMethod.value ?? newPaymentMethod.value;

  if (paymentMethod) {
    if (!props.hasBillingAddress) paymentMethod.billingAddress = newBillingAddress.value;

    emit('handle-update', paymentMethod, fetchPaymentMethods);
  }
};

const handleScroll = () => {
  setTimeout(() => {
    if (paymentMethodList.value) {
      paymentMethodList.value.scroll({
        top: paymentMethodList.value.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, 100);
};

const showBillingAddressForm = computed(
  () => !props.hasBillingAddress && selectedPaymentMethod.value === undefined,
);

const handleClose = () => emit('handle-close');
</script>

<template>
  <Modal
    breakpoint="sm"
    :disableConfirmButton="disabledConfirmationButton"
    :isLoading="isLoading"
    :isOpen="isOpen"
    width="sm:max-w-lg"
    @handle-cancel="handleClose"
    @handle-close="handleClose"
    @handle-confirm="handleConfirm"
    data-test="edit-billing-info-modal"
  >
    <template #header>
      <h3 class="text-2xl leading-7 font-sofia-pro">
        {{ hasBillingAddress ? 'Billing Info' : 'Payment method update' }}
      </h3>
    </template>
    <template #body>
      <p class="mt-6 text-lg escape-legacy">
        <slot name="text">
          <BaseBodyText v-if="declined" class="font-semibold text-nuts-red-800" role="alert">
            Your payment method has been declined. Update your card details or select another
            payment method.
          </BaseBodyText>
          <BaseBodyText v-else> Update your payment details </BaseBodyText>
        </slot>
      </p>
      <div
        v-if="!hasBillingAddress || savedPaymentMethods.length"
        aria-label="Payment Methods Radio Options"
        class="mt-6"
        :class="{
          'overflow-y-auto max-h-96 styled-scrollbar': hasBillingAddress,
          'pr-4': savedPaymentMethods.length > 3 || selectedPaymentMethod === undefined,
        }"
        ref="paymentMethodList"
        role="radiogroup"
      >
        <PaymentMethodOption
          v-for="(paymentMethod, i) in savedPaymentMethods"
          v-model="selectedPaymentMethod"
          class="mt-4 first:mt-0"
          :key="`${paymentMethod.type}-${i}`"
          :id="`${paymentMethod.type}-${i}`"
          :paymentMethod="paymentMethod"
          :variant="
            failedTokenizedPaymentMethod?.nonce === paymentMethod.nonce ? 'declined' : undefined
          "
        />
        <RadioPill
          v-model="selectedPaymentMethod"
          aria-label="Payment method selector"
          class="px-4 py-3 mt-4 md:px-8 md:py-4"
          data-test="payment-method-selector"
          name="payment-method-type"
          :value="undefined"
          @click="handleScroll"
        >
          <div for="new-credit-card" class="w-full font-normal leading-6 md:leading-7">
            Add a new credit/debit card
          </div>
          <template #below>
            <CreditCardForm
              v-if="!hasBillingAddress || defaultOrFirstPaymentMethod?.billingAddress"
              v-show="selectedPaymentMethod === undefined"
              v-model="newPaymentMethod"
              :address="
                hasBillingAddress ? defaultOrFirstPaymentMethod?.billingAddress : newBillingAddress
              "
              :braintreeToken="braintreeToken"
              :canSetAsDefault="canSetAsDefault"
              class="pt-4 pb-4 cursor-default"
            />
            <div v-if="showBillingAddressForm">
              <div class="mt-6 md:mt-8">
                <strong class="text-sm font-semibold leading-4 md:text-base">
                  Billing Address
                </strong>
              </div>
              <AddressForm
                v-model="newBillingAddress"
                class="'mt-6'"
                hideB2BCheckbox
                :includeFormControls="false"
                :parentForm="addressFormModel"
                :stepNumber="4"
                uniqueIdSuffix="billing-address"
              />
            </div>
          </template>
        </RadioPill>
      </div>
      <div v-else class="mt-6 animate-pulse">
        <div
          v-for="item in [1, 2, 3]"
          :key="item"
          class="w-full h-12 mt-4 border-solid first:mt-0 rounded-4xl md:h-14 bg-neutral-200 border-neutral-200"
        />
      </div>
    </template>
    <template #confirm-label>Update</template>
  </Modal>
</template>

<style lang="scss" scoped>
.styled-scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
    height: auto;
    background-color: $alto;
    border-radius: 9999px;
    scrollbar-color: $dark-grey $alto;

    &-thumb {
      background: $dark-grey;
      border-radius: 9999px;
    }
  }
}
</style>
